<template>
  <div v-if="showSection" class="px-2 md:px-0 md:w-1/2 mx-auto mb-8">
    <div class="flex justify-start items-center">
      <div class="text-xl font-bold text-gray-brand-900 tracking-tighter">Payment Methods</div>
    </div>
    <div class="mt-4 md:mt-6 bg-white">
      <div class="rounded-lg border" :class="cardShadowClasses">
        <div class="pt-4 pb-2 px-6">
          <fieldset>
            <div class="leading-5">
              <PaymentMethodRow
                v-for="paymentMethod in paymentMethods"
                :key="paymentMethod.id"
                :payment-method="paymentMethod"
                :is-loading="isDeleting"
                @remove-payment-method="confirmRemovePaymentMethod"
              />
            </div>
          </fieldset>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <Modal :is-open="showConfirmModal" @close="showConfirmModal = false">
      <template #title>
        <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter">Remove Payment Method</h2>
      </template>

      <template #body>
        <div class="sm:flex sm:items-start">
          <div class="w-full">
            <p class="text-gray-900 mb-4">Are you sure you want to remove this payment method?</p>
            <div v-if="paymentMethodToRemove" class="bg-gray-50 rounded-lg p-4">
              <div class="font-semibold text-brand-gray-800">
                {{ getCardTitle(paymentMethodToRemove) }}
              </div>
              <div class="text-sm text-brand-gray-600">
                {{ getCardSubtitle(paymentMethodToRemove) }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="sm:flex sm:flex-row gap-2">
          <button
            type="button"
            class="px-4 py-2 text-white border border-transparent font-semibold text-sm rounded-full bg-red-600 hover:bg-red-700"
            :disabled="isDeleting"
            @click="removePaymentMethod"
          >
            {{ isDeleting ? 'Removing...' : 'Remove' }}
          </button>
          <button
            type="button"
            class="px-4 py-2 text-gray-brand-600 border border-gray-brand-300 font-semibold text-sm rounded-full hover:border-gray-brand-600"
            :disabled="isDeleting"
            @click="showConfirmModal = false"
          >
            Cancel
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import PaymentMethodRow from './PaymentMethodRow.vue'
import Modal from '../Molecules/Modal.vue'
import merchantFetch from '@/utils/merchantFetch'
import { cardShadowClasses } from '@/config'

export default {
  name: 'PaymentMethodsSection',
  components: {
    PaymentMethodRow,
    Modal
  },
  data() {
    return {
      cardShadowClasses,
      paymentMethods: [],
      isLoading: true,
      isDeleting: false,
      showConfirmModal: false,
      paymentMethodToRemove: null
    }
  },
  computed: {
    hasPaymentMethods() {
      return this.paymentMethods && this.paymentMethods.length > 0
    },
    showSection() {
      return !this.isLoading && this.hasPaymentMethods
    }
  },
  async mounted() {
    await this.fetchPaymentMethods()
  },
  methods: {
    async fetchPaymentMethods() {
      this.isLoading = true
      try {
        const response = await merchantFetch('stripe_sources', {
          method: 'GET'
        })
        this.paymentMethods = response.data || []
      } catch (error) {
        console.error('Failed to fetch payment methods:', error)
        this.paymentMethods = []
      } finally {
        this.isLoading = false
      }
    },
    confirmRemovePaymentMethod(paymentMethod) {
      this.paymentMethodToRemove = paymentMethod
      this.showConfirmModal = true
    },
    async removePaymentMethod() {
      if (!this.paymentMethodToRemove) return

      this.isDeleting = true
      try {
        const token = await this.$store.dispatch('getToken', {
          subdomain: window.location.host
        })
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/merchant/stripe_sources/${this.paymentMethodToRemove.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: 'Bearer ' + token
            }
          }
        )
        if (!response.ok) {
          throw new Error('Failed to delete payment method')
        }
        // Remove from local list
        this.paymentMethods = this.paymentMethods.filter((pm) => pm.id !== this.paymentMethodToRemove.id)
        this.showConfirmModal = false
        this.paymentMethodToRemove = null
      } catch (error) {
        console.error('Failed to remove payment method:', error)
      } finally {
        this.isDeleting = false
      }
    },
    getCardTitle(paymentMethod) {
      const brand = paymentMethod?.attributes?.brand?.toString() || ''
      const last4 = paymentMethod?.attributes?.last4?.toString() || ''
      const capitalizedBrand = brand.charAt(0).toUpperCase() + brand.slice(1)
      return `*${last4} ${capitalizedBrand}`
    },
    getCardSubtitle(paymentMethod) {
      const expMonth = paymentMethod?.attributes?.exp_month
      const expYear = paymentMethod?.attributes?.exp_year
      const name = paymentMethod?.attributes?.name

      let subtitle = ''
      if (name) {
        subtitle = name + ' · '
      }
      subtitle += `Expires ${expMonth}/${expYear}`
      return subtitle
    }
  }
}
</script>
